import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
	Badge,
	Button,
	Spinner,
	EmptyState,
	StatusBadge
} from '../ui';
import { RiskVerdict } from './RiskVerdict';
import { ROUTE_PATTERNS, buildRoute } from '../../constants/Paths';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { cn } from '../../utils/cn';

/**
 * ScansTable - Displays scans in a table format using new UI primitives
 * 
 * Pure presentation component that receives scan data and callbacks.
 * Demonstrates the new table architecture with consistent styling.
 */
export const ScansTable = ({
	scans = [],
	onViewScan,
	onRetryScan,
	onCancelScan,
	loading = false,
	className
}) => {
	const navigate = useNavigate();



	const formatDate = (date) => {
		if (!date) return "Never";
		return new Date(date).toLocaleDateString("en-US", {
			month: "2-digit",
			day: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatDuration = (seconds) => {
		if (!seconds) return "N/A";
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}m ${secs}s`;
	};

	const handleViewScan = (scanId) => {
		if (onViewScan) {
			onViewScan(scanId);
		} else {
			navigate(buildRoute(ROUTE_PATTERNS.SCAN_DETAIL, { id: scanId }));
		}
	};

	if (loading) {
		return (
			<div className="w-full p-8 text-center">
				<Spinner size="lg" className="mx-auto" />
				<p className="mt-4 text-muted-foreground">Loading scans...</p>
			</div>
		);
	}

	if (scans.length === 0) {
		return (
			<EmptyState
				compact
				icon={MagnifyingGlassIcon}
				title="No scans yet"
				description="Run a scan to check an account's recent posts for risk."
			/>
		);
	}

	return (
		<div className={cn("space-y-3", className)}>
			{/* Mobile: cards (avoids the wide table overflowing / clipping columns) */}
			<div className="space-y-3 md:hidden">
				{scans.map((scan, index) => (
					<div
						key={scan.id || index}
						onClick={() => handleViewScan(scan.id)}
						className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-input"
					>
						<div className="flex items-start justify-between gap-2">
							<div className="flex items-center gap-3 min-w-0">
								<img src={scan.clientAvatar || "https://placehold.co/32x32"} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
								<div className="min-w-0">
									<div className="font-medium text-foreground truncate">{scan.clientName}</div>
									{scan.accountHandle && <div className="text-sm text-muted-foreground truncate">@{scan.accountHandle}</div>}
								</div>
							</div>
							<StatusBadge status={scan.status} />
						</div>
						<div className="mt-3 flex items-center justify-between gap-2">
							<div className="flex items-center gap-2 min-w-0">
								<Badge variant="outline" className="capitalize">{scan.platform || 'Unknown'}</Badge>
								{scan.status === 'completed' && scan.results ? (
									<RiskVerdict compact results={scan.results} />
								) : (
									<span className="text-xs text-muted-foreground">No risk yet</span>
								)}
							</div>
							<span className="text-xs text-muted-foreground shrink-0">{formatDate(scan.startedAt)}</span>
						</div>
						<div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
							<Button variant="ghost" size="sm" onClick={() => handleViewScan(scan.id)}>View</Button>
							{scan.status === 'failed' && onRetryScan && (
								<Button variant="outline" size="sm" onClick={() => onRetryScan(scan.id)}>Retry</Button>
							)}
							{scan.status === 'in_progress' && onCancelScan && (
								<Button variant="outline" size="sm" onClick={() => onCancelScan(scan.id)}>Cancel</Button>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Desktop: table */}
			<div className="hidden md:block rounded-lg border border-border bg-card overflow-x-auto min-h-[16rem]">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted">
						<TableHead>Client</TableHead>
						<TableHead>Platform</TableHead>
						<TableHead>Scan Type</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Risk</TableHead>
						<TableHead>Started</TableHead>
						<TableHead>Duration</TableHead>
						<TableHead className="text-right">Actions</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{scans.map((scan, index) => (
						<TableRow
							key={scan.id || index}
							className="hover:bg-muted cursor-pointer"
							onClick={() => handleViewScan(scan.id)}
						>
							<TableCell>
								<div className="flex items-center gap-3">
									{scan.clientAvatar && (
										<img
											src={scan.clientAvatar}
											alt={scan.clientName}
											className="w-8 h-8 rounded-full object-cover"
										/>
									)}
									<div>
										<div className="font-medium font-roboto">{scan.clientName}</div>
										{scan.accountHandle && (
											<div className="text-sm text-muted-foreground">@{scan.accountHandle}</div>
										)}
									</div>
								</div>
							</TableCell>

							<TableCell>
								<Badge variant="outline" className="capitalize">
									{scan.platform || 'Unknown'}
								</Badge>
							</TableCell>

							<TableCell>
								<span className="text-sm font-medium capitalize">
									{scan.scanType || 'Standard'}
								</span>
							</TableCell>

							<TableCell>
								{/* StatusBadge shows its own spinner on busy states */}
								<StatusBadge status={scan.status} />
							</TableCell>

							<TableCell>
								{scan.status === 'completed' && scan.results ? (
									<RiskVerdict compact results={scan.results} />
								) : (
									<span className="text-muted-foreground">N/A</span>
								)}
							</TableCell>

							<TableCell>
								<span className="text-sm text-muted-foreground">
									{formatDate(scan.startedAt)}
								</span>
							</TableCell>

							<TableCell>
								<span className="text-sm text-muted-foreground">
									{scan.status === 'completed' ? formatDuration(scan.duration) :
										scan.status === 'in_progress' ? 'Running...' : 'N/A'}
								</span>
							</TableCell>

							<TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
								<div className="flex items-center gap-2 justify-end">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleViewScan(scan.id)}
									>
										View
									</Button>
									{scan.status === 'failed' && onRetryScan && (
										<Button
											variant="outline"
											size="sm"
											onClick={() => onRetryScan(scan.id)}
										>
											Retry
										</Button>
									)}
									{scan.status === 'in_progress' && onCancelScan && (
										<Button
											variant="outline"
											size="sm"
											onClick={() => onCancelScan(scan.id)}
										>
											Cancel
										</Button>
									)}
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			</div>
		</div>
	);
}; 